import { useCallback, useEffect, useState } from 'react';
import {
  Card, CardContent,
  // CardHeader, 
  // CardTitle
} from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  DollarSign,
  FileCheck,
  ArrowRight,
  // Clock,
  TrendingUp,
  // FolderOpen
} from 'lucide-react';
import { FullScreenLoading } from '../../components/ui/loader';
import { toast } from 'sonner';
import { getVisaReqDashboardData } from '../../services/visaService';
import { useScreenPermission } from "hooks/useScreenPermission";
import { usePageHeader } from "contexts/PageHeaderContext";

interface StatusCard {
  id: string;
  title: string;
  count?: number;
  icon: React.ReactNode;
  intensity: 'light' | 'medium' | 'dark';
  page?: string;
}

interface DashboardData {
  visaRequested: number;
  completed: number;
}

interface MuwafiqaProcessingPageProps {
  onNavigate?: (page: string) => void;
}

export const EnhancedMuwafiqaProcessingPage = ({ onNavigate }: MuwafiqaProcessingPageProps) => {
  const { setPageHeader } = usePageHeader();
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null)

  const { screenName, icon: ScreenIcon } = useScreenPermission();


  const statusCards: StatusCard[] = [
    {
      id: 'visa-requested',
      title: 'Visa Requested',
      count: dashboardData?.visaRequested ?? 0,
      icon: <DollarSign className="w-6 h-6" />,
      intensity: 'medium',
      page: 'visa-muwafiqa/visa-document-required'
    },
    {
      id: 'completed',
      title: 'Completed',
      count: dashboardData?.completed ?? 0,
      icon: <FileCheck className="w-6 h-6" />,
      intensity: 'light',
      page: 'visa-muwafiqa/completed'
    }
  ];

  const getColorByIntensity = (intensity: 'light' | 'medium' | 'dark') => {
    switch (intensity) {
      case 'dark':
        return {
          bg: 'rgba(37, 99, 235, 0.08)',
          border: 'rgba(37, 99, 235, 0.25)',
          iconBg: 'rgba(37, 99, 235, 0.15)',
          text: '#2563EB',
          button: '#2563EB'
        };
      case 'medium':
        return {
          bg: 'rgba(91, 155, 213, 0.08)',
          border: 'rgba(91, 155, 213, 0.25)',
          iconBg: 'rgba(91, 155, 213, 0.15)',
          text: '#5B9BD5',
          button: '#5B9BD5'
        };
      case 'light':
        return {
          bg: 'rgba(147, 197, 253, 0.08)',
          border: 'rgba(147, 197, 253, 0.25)',
          iconBg: 'rgba(147, 197, 253, 0.15)',
          text: '#3B82F6',
          button: '#3B82F6'
        };
    }
  };

  const handleCardClick = (card: StatusCard) => {
    if (card.page && onNavigate) {
      onNavigate(card.page);
    }
  };

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getVisaReqDashboardData();
      setDashboardData(data)
    } catch {
      toast.error("Failed to fetch draft visa applications");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setPageHeader({ 
      title: screenName,
      icon: <ScreenIcon className="h-6 w-6" />,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // const totalApplications = statusCards.reduce((sum, card) => sum + card.count, 0);
  // const inProgress = statusCards.slice(0, 2).reduce((sum, card) => sum + card.count, 0);
  // const completed = statusCards[2]?.count || 0;

  return (
    <div className="space-y-6">
      {loading && <FullScreenLoading />}
      {/* Date Filter */}
      <Card className="hidden bg-gradient-to-br from-white via-white to-sky-50/30 border-[#5B9BD5]/30 shadow-xl">
        <CardContent className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                From Date
              </label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="h-12 border-2 border-gray-200 focus:border-[#5B9BD5] transition-colors text-base rounded-xl"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                To Date
              </label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="h-12 border-2 border-gray-200 focus:border-[#5B9BD5] transition-colors text-base rounded-xl"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Status Cards Grid - 2 Cards Only */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" style={{ marginTop: "0px"}}>
        {statusCards.map((card) => {
          const colors = getColorByIntensity(card.intensity);
          return (
            <Card
              key={card.id}
              className="cursor-pointer hover:shadow-xl transition-all hover:scale-[1.02] border-2 rounded-xl"
              style={{
                backgroundColor: colors.bg,
                borderColor: colors.border,
              }}
              onClick={() => handleCardClick(card)}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div
                    className="rounded-xl p-3 shadow-md"
                    style={{ backgroundColor: colors.iconBg }}
                  >
                    <div style={{ color: colors.text }}>
                      {card.icon}
                    </div>
                  </div>
                  {card.count && card.count > 0 && (
                    <div className="flex items-center gap-1 px-2 py-1 rounded-full" style={{ backgroundColor: colors.iconBg }}>
                      <TrendingUp className="w-3 h-3" style={{ color: colors.text }} />
                      <span className="text-xs" style={{ color: colors.text }}>Active</span>
                    </div>
                  )}
                </div>
                <div className="text-center">
                  <div
                    className="mb-2"
                    style={{
                      fontSize: '2.5rem',
                      fontWeight: '700',
                      color: colors.text
                    }}
                  >
                    {card.count}
                  </div>
                  <h3
                    className="text-sm min-h-[40px] flex items-center justify-center leading-tight px-2"
                    style={{ color: colors.text }}
                  >
                    {card.title}
                  </h3>
                </div>
                <div className="mt-5">
                  <Button
                    className="w-full text-white shadow-md rounded-xl hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: colors.button }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCardClick(card);
                    }}
                  >
                    View Details
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Summary Dashboard */}
      {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="bg-gradient-to-br from-white to-purple-50/30 border-purple-200 shadow-xl">
          <CardHeader className="border-b border-purple-100">
            <CardTitle className="flex items-center gap-2 text-purple-900">
              <Clock className="w-5 h-5" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              {[
                { action: 'Fees Collected', ref: 'NK(IR)/TOUR/2025/5415', time: '5 min ago', status: 'success' },
                { action: 'Application Submitted', ref: 'NK(IR)/TOUR/2025/4032', time: '15 min ago', status: 'info' },
                { action: 'Document Uploaded', ref: 'NK(IR)/TOUR/2025/4081', time: '1 hour ago', status: 'warning' }
              ].map((activity, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                  <div className={`w-2 h-2 rounded-full ${activity.status === 'success' ? 'bg-green-500' :
                      activity.status === 'info' ? 'bg-blue-500' : 'bg-orange-500'
                    }`} />
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">{activity.action}</p>
                    <p className="text-xs text-gray-600">{activity.ref}</p>
                  </div>
                  <span className="text-xs text-gray-500">{activity.time}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-white to-sky-50/30 border-sky-200 shadow-xl">
          <CardHeader className="border-b border-sky-100">
            <CardTitle className="flex items-center gap-2" style={{ color: '#5B9BD5' }}>
              <FolderOpen className="w-5 h-5" />
              Opened Applications
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              {[
                { appNo: 'VISA-2025001', name: 'Mohammed Ahmed Khan', appliedDate: 'Dec 1, 2025', status: 'Opened' },
                { appNo: 'VISA-2025015', name: 'Fatima Hassan Ali', appliedDate: 'Dec 2, 2025', status: 'Opened' },
                { appNo: 'VISA-2025028', name: 'Ali Reza Hussain', appliedDate: 'Dec 3, 2025', status: 'Opened' },
                { appNo: 'VISA-2025032', name: 'Zahra Mustafa Sheikh', appliedDate: 'Dec 3, 2025', status: 'Opened' }
              ].map((app, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-sky-200 hover:shadow-md transition-shadow">
                  <div className="w-2 h-2 rounded-full bg-amber-500" />
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">{app.name}</p>
                    <p className="text-xs text-gray-600">{app.appNo}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded-full block mb-1">{app.status}</span>
                    <span className="text-xs text-gray-500">{app.appliedDate}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div> */}
    </div>
  );
};